import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./fleet-map.css";
import { Phone, MessageSquare, Navigation, Route, Clock, Gauge, MapPin, X, Crosshair, AlertTriangle, Loader2, Zap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { dispatchApi, jobsApi, techniciansApi } from "../../lib/api";

const OSRM = process.env.REACT_APP_OSRM_URL || "https://router.project-osrm.org";
const PHX = [33.4484, -112.074];
const mph = (ms) => (ms == null ? null : ms * 2.2369363);
const miles = (m) => m * 0.000621371;
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function statusMeta(t) {
  const js = t.currentJob?.status;
  if (!t.online) return { key: "offline", label: "Offline", color: "#64748b", glow: "rgba(100,116,139,0.5)" };
  if (js === "En Route" || t.status === "En Route") return { key: "enroute", label: "En route", color: "#f59e0b", glow: "rgba(245,158,11,0.8)" };
  if (js === "On Site" || js === "In Progress") return { key: "onsite", label: "On site", color: "#10b981", glow: "rgba(16,185,129,0.8)" };
  if (t.status === "Lunch" || t.status === "Off") return { key: "idle", label: "Idle", color: "#a78bfa", glow: "rgba(167,139,250,0.7)" };
  return { key: "available", label: "Available", color: "#3b82f6", glow: "rgba(59,130,246,0.7)" };
}

function lastSeen(iso) {
  if (!iso) return "never";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function techIcon(t, selected, avatarUrl) {
  const m = statusMeta(t);
  const inner = avatarUrl ? `<img src="${avatarUrl}" alt=""/>` : initials(t.name);
  return L.divIcon({
    className: "",
    html: `<div class="fleet-pin ${selected ? "selected" : ""}" style="--pin-color:${m.color};--pin-glow:${m.glow}">
        <div class="pin-body"><div class="pin-inner">${inner}</div></div>
        <span class="pin-dot ${t.online ? "on" : "off"}"></span>
      </div>`,
    iconSize: selected ? [52, 52] : [34, 34],
    iconAnchor: selected ? [26, 48] : [17, 32],
  });
}
function jobIcon(j) {
  const urgent = (j.priority || "").toLowerCase() === "high" || (j.priority || "").toLowerCase() === "urgent";
  return L.divIcon({
    className: "",
    html: `<div class="fleet-job-pin ${urgent ? "urgent" : ""}"><div class="jp"></div></div>`,
    iconSize: [24, 30], iconAnchor: [12, 28],
  });
}

// jitter identical coords so stacked techs don't perfectly overlap
function jitter(lat, lng, seed) {
  const d = (seed % 7) * 0.00035;
  return [lat + d, lng + (d * 0.8)];
}

function FitBounds({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points.length) return;
    if (points.length === 1) map.setView(points[0], 13);
    else map.fitBounds(L.latLngBounds(points).pad(0.2));
    done.current = true;
  }, [points, map]);
  return null;
}

function Recenter({ points }) {
  const map = useMap();
  return (
    <button
      onClick={() => points.length && map.fitBounds(L.latLngBounds(points).pad(0.2))}
      data-testid="fleet-recenter"
      className="absolute z-[500] top-3 right-3 h-9 w-9 rounded-lg bg-slate-900/90 border border-slate-700 text-slate-200 flex items-center justify-center hover:bg-slate-800"
      title="Recenter"
    ><Crosshair className="h-6 w-6 shrink-0" style={{color: '#ffffff'}} /></button>
  );
}

function RefreshMapButton({ onRefresh, busy }) {
  return (
    <button
      onClick={onRefresh}
      disabled={busy}
      data-testid="fleet-refresh"
      className="absolute z-[500] top-3 right-14 h-9 px-3 rounded-full bg-slate-900/90 border border-slate-700 text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-slate-800 disabled:opacity-60"
      title="Refresh map and technician statuses"
    >
      <RefreshCw className={`h-4 w-4 shrink-0 ${busy ? "animate-spin" : ""}`} style={{color: '#ffffff'}} />
      Refresh Map
    </button>
  );
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "online", label: "Online" },
  { key: "enroute", label: "En route" },
  { key: "onsite", label: "On site" },
  { key: "available", label: "Available" },
  { key: "offline", label: "Offline" },
];

export default function LiveFleetMap() {
  const [data, setData] = useState({ technicians: [], openJobs: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [route, setRoute] = useState(null); // {coords:[[lat,lng]], eta, dist}
  const [optimized, setOptimized] = useState(null); // {coords, order}
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await dispatchApi.live();
      setData(d);
    } catch (e) { /* keep last */ }
    setLoading(false);
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load();
      toast.success("Map refreshed");
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const techs = data.technicians || [];
  const selected = techs.find((t) => t.id === selectedId) || null;

  const visibleTechs = useMemo(() => techs.filter((t) => {
    if (filter === "all") return true;
    if (filter === "online") return t.online;
    return statusMeta(t).key === filter;
  }), [techs, filter]);

  const allPoints = useMemo(() => {
    const pts = [];
    techs.forEach((t) => t.location?.lat != null && pts.push([t.location.lat, t.location.lng]));
    return pts.length ? pts : [PHX];
  }, [techs]);

  // counts
  const counts = useMemo(() => {
    const c = { online: 0, enroute: 0, onsite: 0, late: 0 };
    techs.forEach((t) => {
      if (t.online) c.online++;
      const m = statusMeta(t);
      if (m.key === "enroute") c.enroute++;
      if (m.key === "onsite") c.onsite++;
      if (isLate(t)) c.late++;
    });
    return c;
  }, [techs]);

  // fetch OSRM road route for the selected en-route tech -> job
  useEffect(() => {
    setRoute(null); setOptimized(null);
    const t = selected;
    const job = t?.currentJob;
    if (!t?.location || !job?.coords?.lat) return;
    let cancelled = false;
    const { lat: a, lng: b } = { lat: t.location.lat, lng: t.location.lng };
    const url = `${OSRM}/route/v1/driving/${b},${a};${job.coords.lng},${job.coords.lat}?overview=full&geometries=geojson`;
    fetch(url).then((r) => r.json()).then((j) => {
      if (cancelled || !j.routes?.[0]) return;
      const r0 = j.routes[0];
      setRoute({
        coords: r0.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
        eta: r0.duration, dist: r0.distance,
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedId, selected]);

  const optimizeRoute = async () => {
    const t = selected;
    if (!t) return;
    const jobs = (data.openJobs || []).filter((j) => j.techId === t.id && j.coords?.lat);
    const stops = [t.location, ...jobs.map((j) => j.coords)];
    if (stops.length < 3) { toast.info("Need at least 2 assigned stops to optimize."); return; }
    const coordStr = stops.map((s) => `${s.lng},${s.lat}`).join(";");
    try {
      const res = await fetch(`${OSRM}/trip/v1/driving/${coordStr}?source=first&roundtrip=false&overview=full&geometries=geojson`).then((r) => r.json());
      if (!res.trips?.[0]) { toast.error("Could not optimize route."); return; }
      setOptimized({ coords: res.trips[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]) });
      const km = (res.trips[0].distance / 1000).toFixed(1);
      const min = Math.round(res.trips[0].duration / 60);
      toast.success(`Optimized ${jobs.length} stops · ${km} km · ~${min} min total`);
    } catch (e) { toast.error("Routing service unavailable."); }
  };

  const assignNearest = async (job) => {
    if (!job?.coords?.lat) return;
    const candidates = techs.filter((t) => t.online && t.location?.lat != null);
    if (!candidates.length) { toast.error("No online technicians to assign."); return; }
    let best = null, bestD = Infinity;
    candidates.forEach((t) => {
      const d = haversine(job.coords.lat, job.coords.lng, t.location.lat, t.location.lng);
      if (d < bestD) { bestD = d; best = t; }
    });
    if (!best) return;
    setAssigning(job.id);
    try {
      await jobsApi.patch(job.id, { techId: best.id, tech: best.name, status: "Dispatched" });
      toast.success(`${job.id} assigned to ${best.name} (${miles(bestD * 1000).toFixed(1)} mi away)`);
      load();
    } catch (e) { toast.error("Assign failed."); }
    setAssigning(null);
  };

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-slate-800" style={{ height: "72vh", minHeight: 520 }} data-testid="live-fleet-map">
      {/* Top toolbar */}
      <div className="absolute z-[600] top-3 left-3 right-14 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-900/90 border border-slate-700 rounded-lg p-1">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`fleet-filter-${f.key}`}
              className={`px-2.5 h-7 rounded-md text-[12px] font-semibold transition-colors ${filter === f.key ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[12px]">
          <Badge color="#22c55e" label={`${counts.online} online`} testid="fleet-count-online" />
          <Badge color="#f59e0b" label={`${counts.enroute} en route`} />
          <Badge color="#10b981" label={`${counts.onsite} on site`} />
          {counts.late > 0 && <Badge color="#ef4444" label={`${counts.late} late`} testid="fleet-count-late" icon />}
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 z-[400] bg-slate-950/60 flex items-center justify-center text-slate-300">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      <RefreshMapButton onRefresh={handleRefresh} busy={refreshing} />

      <MapContainer center={PHX} zoom={12} style={{ height: "100%", width: "100%" }} zoomControl={false} preferCanvas>
        <TileLayer
          url={`https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_MAPS_API_KEY}`}
          attribution='&copy; Stadia Maps &copy; OpenMapTiles &copy; OpenStreetMap contributors'
          maxZoom={20}
        />
        <FitBounds points={allPoints} />
        <Recenter points={allPoints} />

        {/* faint straight lines for non-selected active techs -> their job */}
        {visibleTechs.filter((t) => t.id !== selectedId && t.currentJob?.coords?.lat && t.location?.lat).map((t) => (
          <Polyline key={`l-${t.id}`} positions={[[t.location.lat, t.location.lng], [t.currentJob.coords.lat, t.currentJob.coords.lng]]}
            pathOptions={{ className: "route-glow-other", color: "#3b82f6", weight: 2.5, opacity: 0.55, dashArray: "2 7" }} />
        ))}

        {/* selected tech road route (orange) */}
        {route?.coords && (
          <Polyline positions={route.coords} pathOptions={{ className: "route-glow-selected", color: "#f59e0b", weight: 5, opacity: 0.95 }} />
        )}
        {optimized?.coords && (
          <Polyline positions={optimized.coords} pathOptions={{ className: "route-glow-selected", color: "#a855f7", weight: 4, opacity: 0.9, dashArray: "1 8" }} />
        )}

        {/* job pins (open/unassigned) */}
        {(data.openJobs || []).filter((j) => !j.techId).map((j) => (
          <Marker key={`j-${j.id}`} position={[j.coords.lat, j.coords.lng]} icon={jobIcon(j)}
            eventHandlers={{ click: () => assignNearest(j) }}>
            <Tooltip className="fleet-tip" direction="top" offset={[0, -24]}>
              <div>{j.id} · {j.type || "Job"}{j.priority ? ` · ${j.priority}` : ""}<br /><span style={{ opacity: 0.7 }}>{j.address} — click to assign nearest</span></div>
            </Tooltip>
          </Marker>
        ))}

        {/* tech pins */}
        {visibleTechs.map((t, i) => {
          const [la, ln] = jitter(t.location.lat, t.location.lng, i + (t.id.charCodeAt(t.id.length - 1) || 0));
          const av = techniciansApi.avatarUrl(t);
          return (
            <Marker key={t.id} position={[la, ln]} icon={techIcon(t, t.id === selectedId, av)}
              eventHandlers={{ click: () => setSelectedId(t.id === selectedId ? null : t.id) }}>
              <Tooltip className="fleet-tip" direction="top" offset={[0, -34]}>
                <div>
                  <div>{t.name} · {statusMeta(t).label}</div>
                  {t.currentJob && <div style={{ opacity: 0.7 }}>→ {t.currentJob.address || t.currentJob.title}</div>}
                </div>
              </Tooltip>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Tech detail card */}
      {selected && (
        <TechCard tech={selected} route={route} onClose={() => setSelectedId(null)} onOptimize={optimizeRoute}
          hasMultiStops={(data.openJobs || []).filter((j) => j.techId === selected.id && j.coords?.lat).length >= 2} />
      )}

      {assigning && (
        <div className="absolute bottom-3 left-3 z-[600] bg-slate-900/95 border border-slate-700 rounded-lg px-3 py-2 text-[12px] text-slate-200 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Assigning {assigning}…
        </div>
      )}
    </div>
  );
}

function Badge({ color, label, testid, icon }) {
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 px-2 h-7 rounded-md bg-slate-900/90 border border-slate-700 text-slate-200 font-semibold">
      {icon ? <AlertTriangle className="h-3 w-3" style={{ color }} /> : <span className="h-2 w-2 rounded-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />}
      {label}
    </span>
  );
}

function TechCard({ tech, route, onClose, onOptimize, hasMultiStops }) {
  const m = statusMeta(tech);
  const job = tech.currentJob;
  const sp = mph(tech.location?.speed);
  const av = techniciansApi.avatarUrl(tech);
  const late = isLate(tech);
  const etaMin = route?.eta != null ? Math.max(1, Math.round(route.eta / 60)) : null;
  const distMi = route?.dist != null ? miles(route.dist) : (job?.coords?.lat && tech.location?.lat ? miles(haversine(tech.location.lat, tech.location.lng, job.coords.lat, job.coords.lng) * 1000) : null);

  return (
    <div className="absolute z-[600] top-16 right-3 w-[330px] max-w-[88vw] bg-slate-900/95 backdrop-blur border border-slate-700 rounded-xl shadow-2xl text-slate-200" data-testid="fleet-tech-card">
      <div className="flex items-center gap-3 p-3 border-b border-slate-800">
        <div className="h-11 w-11 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center text-[13px] font-bold ring-2" style={{ boxShadow: `0 0 0 2px ${m.color}` }}>
          {av ? <img src={av} alt="" className="h-full w-full object-cover" /> : initials(tech.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[14px] truncate">{tech.name}</div>
          <div className="flex items-center gap-1.5 text-[11.5px]">
            <span className="h-2 w-2 rounded-full" style={{ background: m.color }} />
            <span className="font-medium" style={{ color: m.color }}>{m.label}</span>
            <span className="text-slate-500">· {tech.online ? "live" : `seen ${lastSeen(tech.location?.lastPing)}`}</span>
          </div>
        </div>
        <button onClick={onClose} data-testid="fleet-card-close" className="h-7 w-7 rounded hover:bg-slate-800 flex items-center justify-center"><X className="h-4 w-4 text-slate-400" /></button>
      </div>

      {late && (
        <div className="mx-3 mt-3 flex items-center gap-2 rounded-lg bg-rose-500/15 border border-rose-500/40 px-2.5 py-1.5 text-[12px] text-rose-300 font-medium" data-testid="fleet-late-flag">
          <AlertTriangle className="h-3.5 w-3.5" /> Running behind scheduled arrival
        </div>
      )}

      {/* metrics */}
      <div className="grid grid-cols-3 gap-2 p-3">
        <Metric icon={Clock} label="ETA" value={etaMin != null ? `${etaMin} min` : "—"} accent="#f59e0b" testid="fleet-metric-eta" />
        <Metric icon={Navigation} label="Distance" value={distMi != null ? `${distMi.toFixed(1)} mi` : "—"} accent="#3b82f6" testid="fleet-metric-distance" />
        <Metric icon={Gauge} label="Speed" value={sp != null ? `${Math.round(sp)} mph` : "—"} accent="#10b981" testid="fleet-metric-speed" />
      </div>

      {/* current job */}
      <div className="px-3 pb-2">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Current job</div>
        {job ? (
          <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2.5">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[13px]">{job.id}</span>
              <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-200">{job.status}</span>
            </div>
            <div className="text-[12px] text-slate-300 mt-0.5">{job.type || job.title}{job.customer ? ` · ${job.customer}` : ""}</div>
            <div className="flex items-center gap-1 text-[11.5px] text-slate-400 mt-0.5"><MapPin className="h-3 w-3" /> {job.address}{job.city ? `, ${job.city}` : ""}</div>
            {/* status timeline */}
            <div className="mt-2 space-y-1">
              <TL on={!!job.scheduledStart} label="Scheduled" value={fmtTime(job.scheduledStart)} />
              <TL on={!!job.arrivedAt} label="Arrived on site" value={fmtTime(job.arrivedAt)} />
              <TL on={!!job.workStartedAt} label="Work started" value={fmtTime(job.workStartedAt)} />
              <TL on={!!job.workCompletedAt} label="Completed" value={fmtTime(job.workCompletedAt)} />
            </div>
          </div>
        ) : <div className="text-[12.5px] text-slate-500 italic">No active job.</div>}
      </div>

      {/* vehicle + actions */}
      <div className="px-3 pb-3 space-y-2">
        {tech.vehicle && <div className="text-[11.5px] text-slate-400">🚐 {tech.vehicle}</div>}
        <div className="flex gap-2">
          <a href={tech.phone ? `tel:${tech.phone}` : undefined} data-testid="fleet-call-btn"
            className={`flex-1 h-9 rounded-lg flex items-center justify-center gap-1.5 text-[12.5px] font-semibold ${tech.phone ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-slate-800 text-slate-500 pointer-events-none"}`}>
            <Phone className="h-3.5 w-3.5" /> Call
          </a>
          <a href={tech.phone ? `sms:${tech.phone}` : undefined} data-testid="fleet-text-btn"
            className={`flex-1 h-9 rounded-lg flex items-center justify-center gap-1.5 text-[12.5px] font-semibold ${tech.phone ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-slate-800 text-slate-500 pointer-events-none"}`}>
            <MessageSquare className="h-3.5 w-3.5" /> Text
          </a>
        </div>
        {hasMultiStops && (
          <button onClick={onOptimize} data-testid="fleet-optimize-btn"
            className="w-full h-9 rounded-lg bg-purple-600 hover:bg-purple-500 text-white flex items-center justify-center gap-1.5 text-[12.5px] font-semibold">
            <Route className="h-3.5 w-3.5" /> Optimize today's route
          </button>
        )}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="rounded-lg bg-slate-800/60 border border-slate-700 px-2 py-2 text-center" data-testid={testid}>
      <Icon className="h-3.5 w-3.5 mx-auto mb-0.5" style={{ color: accent }} />
      <div className="text-[13px] font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

function TL({ on, label, value }) {
  return (
    <div className="flex items-center gap-2 text-[11.5px]">
      <span className={`h-2 w-2 rounded-full ${on ? "bg-emerald-400" : "bg-slate-600"}`} />
      <span className={on ? "text-slate-300" : "text-slate-500"}>{label}</span>
      <span className="ml-auto text-slate-400">{value || "—"}</span>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return null;
  const d = new Date(iso.length <= 16 ? `${iso}:00` : iso);
  if (isNaN(d)) return null;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function isLate(t) {
  const job = t.currentJob;
  if (!job?.scheduledStart || (job.status !== "En Route" && job.status !== "Dispatched")) return false;
  const sched = new Date(job.scheduledStart.length <= 16 ? `${job.scheduledStart}:00` : job.scheduledStart);
  return !isNaN(sched) && Date.now() > sched.getTime();
}
function haversine(aLat, aLng, bLat, bLng) {
  const R = 6371, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(bLat - aLat), dLng = rad(bLng - aLng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
}
